import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { CartModification } from '@spartacus/cart/base/root';
import { GlobalMessageService, GlobalMessageType, LoggerService, tryNormalizeHttpError, SiteContextActions, withdrawOn } from '@spartacus/core';
import { from, Observable } from 'rxjs';
import { catchError, concatMap, map, switchMap, tap } from 'rxjs/operators';
import { GenCartEntryConnector } from '../../connectors/entry/cart-entry.connector';
import { CartActions } from '../actions/index';
import { GenBaseStoreService } from 'src/app/generac/services/base-store.service';
import { GenNotification } from 'src/app/generac/models/notification.model';
import { GenLimitedCartModification } from 'src/app/generac/utils/limited-availability.utils';

@Injectable()
export class GenCartEntryEffects {
  private contextChange$ = this.actions$.pipe(
    ofType(
      SiteContextActions.CURRENCY_CHANGE,
      SiteContextActions.LANGUAGE_CHANGE
    )
  );

  addEntry$: Observable<
    | CartActions.GenCartAddEntrySuccess
    | CartActions.GenCartAddEntryFail
    | CartActions.LoadCart
  > = createEffect(() =>
    this.actions$.pipe(
      ofType(CartActions.GEN_CART_ADD_ENTRY),
      map((action: CartActions.GenCartAddEntry) => action.payload),
      concatMap((payload) => {
        return this.cartEntryConnector
          .add(
            payload.userId,
            payload.cartId,
            payload.productCode,
            payload.quantity,
            payload.itemListName,
            payload.navigatedToPdpFrom,
            payload.entrySource
          )
          .pipe(
            map(
              (cartModification: CartModification) => {
                return new CartActions.GenCartAddEntrySuccess({
                  ...payload,
                  ...(cartModification as GenLimitedCartModification),
                })
              }
            ),
            catchError((error) => {
              return this.baseStoreService.getPhoneNumber()
                .pipe(
                  tap(phoneNumber => {
                    if (!error?.error?.generacNotifications?.some((n: GenNotification) => n.type === 'POPUP')) {
                      const message = error?.error?.message ? error.error.message : {
                        key: 'httpHandlers.domainError',
                        params: { phoneNumber: phoneNumber.contactInfo }
                      };

                      this.globalMessageService.add(
                        message,
                        GlobalMessageType.MSG_TYPE_ERROR
                      );
                    }
                  }),
                  switchMap(() => from([
                    new CartActions.GenCartAddEntryFail({
                      ...payload,
                      error: tryNormalizeHttpError(error, this.logger),
                    }),
                    new CartActions.LoadCart({
                      cartId: payload.cartId,
                      userId: payload.userId,
                    }),
                  ]))
                )
            })
          );
      }),
      withdrawOn(this.contextChange$)
    )
  );

  removeEntry$: Observable<
    | CartActions.GenCartRemoveEntrySuccess
    | CartActions.GenCartRemoveEntryFail
    | CartActions.LoadCart
  > = createEffect(() =>
    this.actions$.pipe(
      ofType(CartActions.GEN_CART_REMOVE_ENTRY),
      map((action: CartActions.GenCartRemoveEntry) => action.payload),
      concatMap((payload) =>
        this.cartEntryConnector
          .remove(payload.userId, payload.cartId, payload.entryNumber)
          .pipe(
            map(() => {
              return new CartActions.GenCartRemoveEntrySuccess({
                ...payload,
              });
            }),
            catchError((error) =>
              from([
                new CartActions.GenCartRemoveEntryFail({
                  ...payload,
                  error: tryNormalizeHttpError(error, this.logger),
                }),
                new CartActions.LoadCart({
                  cartId: payload.cartId,
                  userId: payload.userId,
                }),
              ])
            )
          )
      ),
      withdrawOn(this.contextChange$)
    )
  );

  updateEntry$: Observable<
    | CartActions.GenCartUpdateEntrySuccess
    | CartActions.GenCartUpdateEntryFail
    | CartActions.LoadCart
  > = createEffect(() =>
    this.actions$.pipe(
      ofType(CartActions.GEN_CART_UPDATE_ENTRY),
      map((action: CartActions.GenCartUpdateEntry) => action.payload),
      concatMap((payload) =>
        this.cartEntryConnector
          .update(
            payload.userId,
            payload.cartId,
            payload.entryNumber,
            payload.quantity
          )
          .pipe(
            map((cartModification: CartModification) => {
              return new CartActions.GenCartUpdateEntrySuccess({
                ...payload,
                ...(cartModification as GenLimitedCartModification),
              });
            }),
            catchError((error) =>
              from([
                new CartActions.GenCartUpdateEntryFail({
                  ...payload,
                  error: tryNormalizeHttpError(error, this.logger),
                }),
                new CartActions.LoadCart({
                  cartId: payload.cartId,
                  userId: payload.userId,
                }),
              ])
            )
          )
      ),
      withdrawOn(this.contextChange$)
    )
  );

  constructor(
    private actions$: Actions,
    private cartEntryConnector: GenCartEntryConnector,
    private globalMessageService: GlobalMessageService,
    private baseStoreService: GenBaseStoreService,
    private logger: LoggerService,
  ) { }
}
